/**
 * Team management (multi-tenancy Phase F, Slice 4) — the Admin → Team tab.
 *
 * Lists the active org's members with inline role changes + removal, the
 * pending invites with revoke, and an Invite sheet that creates an invite
 * (emailing the link when SMTP is live, always surfacing a copy-link
 * fallback). Admin-only — the Admin route only renders this tab when
 * useOrg().canAdmin, and the backend re-enforces RequireRole(admin).
 */

import { useCallback, useEffect, useState } from "react";
import {
  Check,
  Copy,
  Crown,
  Loader2,
  Mail,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";

import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";
import { FOUNDRY_SCOPE, FOUNDRY_SCOPE_ROLES, FOUNDRY_SCOPE_ROLE_LABELS, heldProduct } from "@/lib/suite";
import { useToast } from "@/lib/ui/toast";
import { ApiClientError } from "@/lib/api-client";
import { membersApi, invitesApi, type Member, type Invite } from "@/lib/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/lib/ui/card";
import { Button } from "@/lib/ui/button";
import { Badge } from "@/lib/ui/badge";
import { Input } from "@/lib/ui/input";
import { Sheet, SheetHeader } from "@/lib/ui/sheet";
import { cn } from "@/lib/ui/cn";

// The roles a team admin can grant (mirrors orgctx.assignableRoles on the backend).
// "owner" is intentionally absent — ownership is the is_owner crown (the locked row
// with the Crown), set at org creation / transfer, not a dropdown choice.
const ROLES = [
  { value: "admin", label: "Admin", hint: "Manages the org, members, branding, and all data" },
  { value: "member", label: "Member", hint: "Full read + write across data and the twin model" },
  { value: "operator", label: "Operator", hint: "Floor data entry (mould / pattern / QC) + read-only views" },
  { value: "technician", label: "Technician", hint: "Commissions gateways + read-only views" },
  { value: "viewer", label: "Viewer", hint: "Read-only access to everything" },
] as const;
type RoleValue = (typeof ROLES)[number]["value"];

/** Map a team API failure to a friendly message (backend guard codes). */
function humanError(e: unknown, fallback = "Something went wrong. Please try again."): string {
  if (e instanceof ApiClientError) {
    const code = (e.body as { error?: string } | null)?.error;
    const map: Record<string, string> = {
      last_admin: "An org must keep at least one admin.",
      cannot_remove_owner: "The org owner can't be removed.",
      cannot_change_owner_role: "The org owner's role can't be changed.",
      member_not_found: "That member no longer exists.",
      invite_not_found: "That invite no longer exists.",
      invalid_role: "Pick a valid role.",
      missing_required_field: "Email is required.",
      product_not_held: "The organisation no longer holds Foundry Scope.",
    };
    if (code && map[code]) return map[code];
    if (e.status === 403) return "You don't have permission to do that.";
  }
  return fallback;
}

export function TeamsPanel() {
  const { activeOrgId, activeOrg } = useOrg();
  const { user, refreshIdentity } = useAuth();
  // Foundry Scope roles are set here when the org holds the product (plan of record, section A).
  const scope = heldProduct(activeOrg?.products, FOUNDRY_SCOPE);
  const toast = useToast();

  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const reload = useCallback(async () => {
    if (!activeOrgId) return;
    setLoading(true);
    setError(null);
    try {
      const [m, i] = await Promise.all([
        membersApi.list(activeOrgId),
        invitesApi.list(activeOrgId),
      ]);
      setMembers(m.items);
      setInvites(i.items);
    } catch (e) {
      setError(humanError(e, "Couldn't load the team."));
    } finally {
      setLoading(false);
    }
  }, [activeOrgId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const myEmail = (user?.email ?? "").toLowerCase();

  const changeRole = async (m: Member, role: string) => {
    if (!activeOrgId || role === m.role) return;
    setBusyId(m.user_id);
    try {
      await membersApi.updateRole(activeOrgId, m.user_id, role);
      toast.push("ok", `${m.email} is now ${role}.`);
      await reload();
    } catch (e) {
      toast.push("error", humanError(e));
    } finally {
      setBusyId(null);
    }
  };

  const changeScopeRole = async (m: Member, role: string) => {
    const current = m.product_roles?.[FOUNDRY_SCOPE] ?? "";
    if (!activeOrgId || role === current) return;
    setBusyId(m.user_id);
    try {
      if (role) {
        await membersApi.setProductRole(activeOrgId, m.user_id, FOUNDRY_SCOPE, role);
        toast.push("ok", `${m.email} is now a Foundry Scope ${role}.`);
      } else {
        await membersApi.removeProductRole(activeOrgId, m.user_id, FOUNDRY_SCOPE);
        toast.push("ok", `${m.email} no longer has Foundry Scope.`);
      }
      // A change to one's own role changes the app switcher: read /auth/me again.
      if (m.email.toLowerCase() === myEmail) await refreshIdentity();
      await reload();
    } catch (e) {
      toast.push("error", humanError(e));
    } finally {
      setBusyId(null);
    }
  };

  const removeMember = async (m: Member) => {
    if (!activeOrgId) return;
    setBusyId(m.user_id);
    try {
      await membersApi.remove(activeOrgId, m.user_id);
      toast.push("ok", `Removed ${m.email}.`);
      await reload();
    } catch (e) {
      toast.push("error", humanError(e));
    } finally {
      setBusyId(null);
    }
  };

  const revokeInvite = async (inv: Invite) => {
    if (!activeOrgId) return;
    setBusyId(inv.id);
    try {
      await invitesApi.revoke(activeOrgId, inv.id);
      toast.push("ok", `Revoked the invite for ${inv.email}.`);
      await reload();
    } catch (e) {
      toast.push("error", humanError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start gap-3">
          <div className="flex size-9 items-center justify-center rounded-md bg-brand-muted text-brand">
            <Users className="size-5" />
          </div>
          <div className="flex-1">
            <CardTitle>Team</CardTitle>
            <CardDescription>
              Invite teammates and manage their roles — admins, members, floor
              operators, field technicians, and read-only viewers.
            </CardDescription>
          </div>
          <Button size="sm" onClick={() => setInviteOpen(true)}>
            <UserPlus className="mr-1.5 size-4" />
            Invite
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {loading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-on-surface-muted">
              <Loader2 className="size-4 animate-spin" /> Loading team…
            </div>
          ) : error ? (
            <div className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
              {error}
            </div>
          ) : (
            <>
              {/* Members */}
              <div className="flex flex-col gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
                  Members ({members.length})
                </span>
                {scope && (
                  <p className="text-xs text-on-surface-muted">
                    The second role on each row is the member's access to Foundry Scope, the metallography product.
                    {!scope.usable && " The organisation's Foundry Scope is not active, so these roles open nothing until it is."}
                  </p>
                )}
                <div className="overflow-hidden rounded-md border border-border">
                  {members.map((m, idx) => {
                    const isSelf = m.email.toLowerCase() === myEmail;
                    const locked = m.is_owner || isSelf;
                    // Show the member's current role even when it isn't a grantable
                    // option (e.g. a legacy "owner"), so the locked select reflects
                    // reality instead of silently defaulting to the first option.
                    const roleOpts = ROLES.some((r) => r.value === m.role)
                      ? ROLES.map((r) => ({ value: r.value as string, label: r.label }))
                      : [
                          { value: m.role, label: m.role },
                          ...ROLES.map((r) => ({ value: r.value as string, label: r.label })),
                        ];
                    return (
                      <div
                        key={m.user_id}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2.5",
                          idx > 0 && "border-t border-border",
                        )}
                      >
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="flex items-center gap-1.5 truncate text-sm font-medium text-on-surface">
                            {m.display_name || m.email}
                            {m.is_owner && (
                              <Crown className="size-3.5 text-warning" aria-label="Owner" />
                            )}
                            {isSelf && (
                              <Badge variant="secondary" className="text-[9px]">
                                you
                              </Badge>
                            )}
                          </span>
                          <span className="truncate text-xs text-on-surface-muted">
                            {m.email}
                          </span>
                        </div>
                        <select
                          value={m.role}
                          aria-label={`Foundry Twin role of ${m.email}`}
                          disabled={locked || busyId === m.user_id}
                          onChange={(e) => void changeRole(m, e.target.value)}
                          className="h-8 rounded-md border border-border bg-surface px-2 text-xs capitalize text-on-surface disabled:opacity-60"
                        >
                          {roleOpts.map((r) => (
                            <option key={r.value} value={r.value}>
                              {r.label}
                            </option>
                          ))}
                        </select>
                        {scope && (
                          <ScopeRoleSelect
                            member={m}
                            disabled={busyId === m.user_id}
                            onChange={(role) => void changeScopeRole(m, role)}
                          />
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={`Remove ${m.email}`}
                          disabled={locked || busyId === m.user_id}
                          onClick={() => void removeMember(m)}
                        >
                          {busyId === m.user_id ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <Trash2 className="size-4" />
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pending invites */}
              {invites.length > 0 && (
                <div className="flex flex-col gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
                    Pending invites ({invites.length})
                  </span>
                  <div className="overflow-hidden rounded-md border border-border">
                    {invites.map((inv, idx) => (
                      <div
                        key={inv.id}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2.5",
                          idx > 0 && "border-t border-border",
                        )}
                      >
                        <Mail className="size-4 shrink-0 text-on-surface-muted" />
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate text-sm text-on-surface">
                            {inv.email}
                          </span>
                          <span className="text-xs text-on-surface-muted">
                            invited as {inv.role}
                          </span>
                        </div>
                        <Badge variant="warning" className="text-[10px]">
                          pending
                        </Badge>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busyId === inv.id}
                          onClick={() => void revokeInvite(inv)}
                        >
                          {busyId === inv.id ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            "Revoke"
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <InviteSheet
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        orgId={activeOrgId}
        onInvited={reload}
      />
    </>
  );
}

/** A member's Foundry Scope role, or none (org_member_suite_roles; lib/suite). */
export function ScopeRoleSelect({
  member,
  disabled,
  onChange,
}: {
  member: Pick<Member, "email" | "product_roles">;
  disabled: boolean;
  onChange: (role: string) => void;
}) {
  return (
    <select
      value={member.product_roles?.[FOUNDRY_SCOPE] ?? ""}
      aria-label={`Foundry Scope role of ${member.email}`}
      title="Foundry Scope"
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 rounded-md border border-border bg-surface px-2 text-xs text-on-surface disabled:opacity-60"
    >
      <option value="">No Foundry Scope</option>
      {FOUNDRY_SCOPE_ROLES.map((r) => (
        <option key={r} value={r}>
          Scope {FOUNDRY_SCOPE_ROLE_LABELS[r].toLowerCase()}
        </option>
      ))}
    </select>
  );
}

function InviteSheet({
  open,
  onClose,
  orgId,
  onInvited,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  onInvited: () => Promise<void> | void;
}) {
  const toast = useToast();
  const { activeOrg } = useOrg();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<RoleValue>("member");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setEmail("");
    setRole("member");
    setLink(null);
    setCopied(false);
  };

  const close = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    if (!orgId || !email.trim() || busy) return;
    setBusy(true);
    try {
      const resp = await invitesApi.create(orgId, { email: email.trim(), role });
      setLink(resp.link);
      toast.push("ok", `Invite created for ${resp.invite.email}.`);
      await onInvited();
    } catch (e) {
      toast.push("error", humanError(e, "Couldn't create the invite."));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the link text is still selectable */
    }
  };

  return (
    <Sheet open={open} onClose={close} ariaLabel="Invite a teammate">
      <SheetHeader title="Invite a teammate" onClose={close} />
      <div className="flex flex-col gap-4 p-5">
        {!link ? (
          <>
            <p className="rounded-md border border-border bg-surface-muted px-3 py-2 text-xs text-on-surface-muted">
              Inviting to{" "}
              <span className="font-semibold text-on-surface">
                {activeOrg?.orgName ?? "this organisation"}
              </span>
              . They'll join this org with the role you pick below.
            </p>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
                Email
              </span>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="teammate@company.com"
                autoFocus
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
                Role
              </span>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as RoleValue)}
                className="h-9 w-full rounded-md border border-border bg-surface px-2 text-sm capitalize text-on-surface"
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              <span className="text-[11px] text-on-surface-muted">
                {ROLES.find((r) => r.value === role)?.hint}
              </span>
            </label>
            <div className="flex justify-end gap-2 border-t border-border pt-4">
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button onClick={() => void submit()} disabled={busy || !email.trim()}>
                {busy && <Loader2 className="mr-1.5 size-4 animate-spin" />}
                Send invite
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-start gap-2 rounded-md border border-success/40 bg-success/10 p-3 text-sm text-success">
              <Check className="mt-0.5 size-4 shrink-0" />
              <span>
                Invite created. We've emailed the link (if email delivery is
                live) — you can also copy it below and share it directly.
              </span>
            </div>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
                Accept link
              </span>
              <div className="flex items-center gap-2">
                <Input value={link} readOnly className="font-mono text-xs" />
                <Button size="sm" variant="outline" onClick={() => void copyLink()} className="shrink-0">
                  {copied ? (
                    <>
                      <Check className="mr-1.5 size-3.5" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="mr-1.5 size-3.5" /> Copy
                    </>
                  )}
                </Button>
              </div>
            </label>
            <div className="flex justify-end gap-2 border-t border-border pt-4">
              <Button variant="ghost" onClick={reset}>
                Invite another
              </Button>
              <Button onClick={close}>Done</Button>
            </div>
          </>
        )}
      </div>
    </Sheet>
  );
}
